import {
  Autocomplete,
  Card,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  Button,
  Link,
  FormControlLabel,
  Checkbox,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Icon } from "@iconify/react";
import CircleProgressBar from "../../components/application-utils/CircleProgressBar";
import HeaderBreadcrumbs from "../../core/components/HeaderBreadcrumbs";
import { LoadingButton } from "@mui/lab";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Img, GridFlexStart, GridFlexEnd } from "../../components/styled";
import {
  getSupportedLanguages,
  getFileFormats,
  getRecordConvertTypes,
} from "../../services/common.service";
import baseService from "../../services/base.service";
import {
  HelpOutline,
  QuestionMarkOutlined,
  QuestionMarkRounded,
} from "@mui/icons-material";
import { AzureEntraContext } from "../../contexts/AzureEntraContext";
import useAuth from "../../hooks/useAuth";

/** Hugging Face model ids used by batch faster_whisper. */
const WHISPER_MODEL_IDS = {
  turbo: "Purfview/faster-whisper-large-v3-turbo",
  largeV3: "Systran/faster-whisper-large-v3",
} as const;

const DEFAULT_RECORD_CONVERT_TYPE = "docx";
const DEFAULT_LANGUAGE = "tr";

export default function UploadAudioFile() {
  const [file, setFile] = useState<File | null>(null);
  const [isFileSubmitted, setFileSubmit] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const [fileTypes, setFileTypes] = useState<any>([]);
  const [languages, setLanguages] = useState<any>([]);
  const [acceptedFiles, setAcceptedFiles] = useState<string>();
  const [checked, setChecked] = useState(false);
  const [shareWithDepartment, setShareWithDepartment] = useState(true);
  const { companyInfo } = useAuth();
  const defaultConvertKeyRef = useRef<string>(DEFAULT_RECORD_CONVERT_TYPE);
  const defaultConvertInitializedRef = useRef(false);
  const defaultLanguageKeyRef = useRef<string>(DEFAULT_LANGUAGE);
  const defaultLanguageInitializedRef = useRef(false);

  const handleGetSupportedLanguages = async () => {
    const res = await getSupportedLanguages();
    if (res?.status === 200) {
      const keys: any = [];
      res?.data?.data.map((item: any) => keys.push(item?.key));
      setLanguages(keys);
    } else {
      enqueueSnackbar("Diller getirilirken hata oluştu!", {
        variant: "error",
      });
    }
  };

  const handleGetRecordConvertTypes = async () => {
    const res = await getRecordConvertTypes();
    if (res?.status === 200) {
      const keys: any = [];
      res?.data?.data.map((item: any) => keys.push(item?.key));
      setFileTypes(keys);
    } else {
      enqueueSnackbar("Kayıt tipleri getirilirken hata oluştu!", {
        variant: "error",
      });
    }
  };

  const handleGetFileFormats = async () => {
    const res = await getFileFormats();
    if (res?.status === 200) {
      res?.data?.data.map((item: any) =>
        setAcceptedFiles((type) => type + "," + item?.mimeType)
      );
    } else {
      enqueueSnackbar("Kayıt tipleri getirilirken hata oluştu!", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    handleGetSupportedLanguages();
    handleGetFileFormats();
    handleGetRecordConvertTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convertSizeToMB = (fileSize: any) => {
    return (parseFloat(fileSize ?? "0") / Math.pow(10, 6)).toFixed(2);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  type InitialValues = {
    fileName: string;
    description: string;
    fileFormat: string;
    language: string;
    engine: "speechmatics" | "whisper";
  };
  const UploadSchema = Yup.object().shape({
    fileName: Yup.string().required("İsim alanı zorunludur"),
    description: Yup.string().required("Açıklama alanı zorunludur"),
    fileFormat: Yup.string().required("Format alanı zorunludur"),
    language: Yup.string().required("Dil alanı zorunludur"),
  });
  const formik = useFormik<InitialValues>({
    initialValues: {
      fileName: "",
      description: "",
      fileFormat: DEFAULT_RECORD_CONVERT_TYPE,
      language: DEFAULT_LANGUAGE,
      engine: "speechmatics",
    },
    validationSchema: UploadSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      if (!file) {
        enqueueSnackbar("Lütfen dosya seçiniz!", { variant: "error" });
      } else {
        try {
          const formData = new FormData();
          file && formData.append("file", file);
          formData.append("record_name", values.fileName);
          formData.append("description", values.description);
          formData.append("ConvertType", values.fileFormat);
          formData.append("Language", values.language);
          formData.append("is_secret", checked ? "true" : "false");
          formData.append(
            "share_with_department",
            shareWithDepartment ? "true" : "false",
          );
          formData.append("use_rich_result", "true");
          formData.append("email", companyInfo?.user_email || "");

          const isWhisper = values.engine === "whisper";
          formData.append(
            "engine",
            isWhisper ? "faster_whisper" : "speechmatics",
          );
          if (isWhisper) {
            formData.append(
              "engine_config",
              JSON.stringify({
                model: WHISPER_MODEL_IDS.largeV3,
                diarize: true,
                language: values.language,
              }),
            );
          }

          setFileSubmit(true);
          await baseService.post("/record/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent: any) => {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              setProgress(progress);
            },
            // timeout: 30000,
          });
          enqueueSnackbar("Dosya başarıyla iletildi!", {
            variant: "success",
          });
          setFileSubmit(false);
          setFile(null);
          setProgress(0);
          resetForm({
            values: {
              fileName: "",
              description: "",
              fileFormat: defaultConvertKeyRef.current,
              language: defaultLanguageKeyRef.current,
              engine: "speechmatics",
            },
          });
          // setFieldValue('fileFormat', '');
          // setFieldValue('language', '');
        } catch (error: any) {
          if (error?.code === 400) {
            enqueueSnackbar(
              "Dosya yüklenirken hata meydana geldi: " + error?.message,
              {
                variant: "error",
                autoHideDuration: 6000,
              }
            );
          } else {
            enqueueSnackbar("Dosya yüklenirken hata meydana geldi.", {
              variant: "error",
              autoHideDuration: 6000,
            });
          }
          console.error(error);
          setFileSubmit(false);
          setProgress(0);
          setSubmitting(false);
          setErrors(error);
        }
      }
    },
  });
  const {
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    values,
  } = formik;

  useEffect(() => {
    if (!fileTypes.length || defaultConvertInitializedRef.current) return;
    const match = fileTypes.find(
      (k: string) =>
        String(k).toLowerCase() === DEFAULT_RECORD_CONVERT_TYPE,
    );
    if (match) {
      defaultConvertKeyRef.current = match;
      setFieldValue("fileFormat", match);
      defaultConvertInitializedRef.current = true;
    }
  }, [fileTypes, setFieldValue]);

  useEffect(() => {
    if (!languages.length || defaultLanguageInitializedRef.current) return;
    const match = languages.find(
      (k: string) => String(k).toLowerCase() === DEFAULT_LANGUAGE,
    );
    if (match) {
      defaultLanguageKeyRef.current = match;
      setFieldValue("language", match);
      defaultLanguageInitializedRef.current = true;
    }
  }, [languages, setFieldValue]);

  return (
    <Container>
      <HeaderBreadcrumbs
        heading="Ses Dosyası Yükle"
        links={[
          { name: "Dashboard", href: "/" },
          { name: "Ses Dosyası Yükleme" },
        ]}
      />
      <FormikProvider value={formik}>
        <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
          <Card sx={{ p: 3 }}>
            <Stack spacing={{ xs: 2, md: 3 }}>
              <Stack>
                <TextField
                  {...getFieldProps("fileName")}
                  fullWidth
                  label="Dosya İsmi"
                  key="fileName"
                  error={Boolean(touched.fileName && errors.fileName)}
                  helperText={touched.fileName && errors.fileName}
                  required
                />
              </Stack>
              <Stack>
                <TextField
                  {...getFieldProps("description")}
                  fullWidth
                  label="Toplantı Açıklama"
                  key="description"
                  error={Boolean(touched.description && errors.description)}
                  helperText={touched.description && errors.description}
                  multiline
                  minRows={6}
                  required
                />
              </Stack>
              {isFileSubmitted ? (
                <Stack>
                  <Grid container alignItems="center" spacing={2} marginTop={2}>
                    <Grid item>
                      <CircleProgressBar value={progress} />
                    </Grid>
                    <Grid item xs={12} direction="row" sm container>
                      <Grid item xs={10}>
                        <Typography color="#919EAB" fontSize={24}>
                          "{file?.name}" isimli dosya{" "}
                          {isSuccess ? "başarıyla yüklendi!" : "yükleniyor..."}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Stack>
              ) : (
                <>
                  {file ? (
                    <>
                      <Stack>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item xs={2}>
                            <Img
                              alt="illustration"
                              src="/illustrations/file.png"
                            />
                          </Grid>
                          <Grid item xs={8}>
                            <Typography>{file?.name}</Typography>
                            <Typography>
                              {convertSizeToMB(file?.size)} MB
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <GridFlexEnd>
                              <Button
                                color="error"
                                onClick={() => setFile(null)}
                              >
                                <Icon icon="akar-icons:trash-can" />
                                <Typography>Sil</Typography>
                              </Button>
                            </GridFlexEnd>
                          </Grid>
                        </Grid>
                      </Stack>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<Icon icon="mdi:plus" />}
                    >
                      Ses Dosyası Seçin
                      <input
                        type="file"
                        accept={acceptedFiles}
                        onChange={handleFileChange}
                        hidden
                      />
                    </Button>
                  )}
                </>
              )}

              <Stack>
                <FormControl fullWidth>
                  <InputLabel id="engine-label">Çeviri motoru</InputLabel>
                  <Select
                    labelId="engine-label"
                    label="Çeviri motoru"
                    value={values.engine}
                    onChange={(e) =>
                      setFieldValue(
                        "engine",
                        e.target.value as "speechmatics" | "whisper",
                      )
                    }
                  >
                    <MenuItem value="speechmatics">Boardnote</MenuItem>
                    <MenuItem value="whisper">Whisper</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack>
                <Grid container direction="row" spacing={4} marginTop={1}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      disablePortal
                      options={fileTypes}
                      fullWidth
                      value={values.fileFormat || ""}
                      onChange={(e, value) =>
                        setFieldValue("fileFormat", value)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          {...getFieldProps("fileFormat")}
                          key="fileFormat"
                          label="Dosyanın Çevrileceği Format"
                          error={Boolean(
                            touched.fileFormat && errors.fileFormat
                          )}
                          helperText={touched.fileFormat && errors.fileFormat}
                          required
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      disablePortal
                      value={values.language || ""}
                      options={languages}
                      fullWidth
                      onChange={(e, value) => setFieldValue("language", value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          {...getFieldProps("language")}
                          key="language"
                          error={Boolean(touched.language && errors.language)}
                          helperText={touched.language && errors.language}
                          label="Dosyanın Dil Formatı"
                          required
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>

              <Stack direction="column" alignItems="flex-start" spacing={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={shareWithDepartment}
                        onChange={() =>
                          setShareWithDepartment(!shareWithDepartment)
                        }
                        name="share_with_department"
                        color="primary"
                      />
                    }
                    label="Departman ile paylaş"
                  />
                  <Tooltip title="İşaretliyse aynı departmandaki kullanıcılar bu kaydı görebilir">
                    <HelpOutline />
                  </Tooltip>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => setChecked(!checked)}
                        name="checked"
                        color="primary"
                      />
                    }
                    label="Dosya Gizli Bilgi İçeriyor"
                  />
                  <Tooltip title="Bu seçeneği işaretlerseniz üretilen dosya ilk indirmeden sonra silinecektir">
                    <HelpOutline />
                  </Tooltip>
                </Stack>
              </Stack>

              <Stack>
                <Grid container marginTop={4}>
                  <GridFlexStart item xs={6}>
                    <Button variant="outlined" color="inherit">
                      <Link
                        href={"/"}
                        underline="none"
                        color="inherit"
                        rel="cancel"
                      >
                        İptal
                      </Link>
                    </Button>
                  </GridFlexStart>
                  <GridFlexEnd item xs={6}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      loading={isSubmitting}
                    >
                      Kaydet
                    </LoadingButton>
                  </GridFlexEnd>
                </Grid>
              </Stack>
            </Stack>
          </Card>
        </Form>
      </FormikProvider>
    </Container>
  );
}
